import { fetchSessions } from "@/services/heygen/dashboard/fetchSessions";
import type { TSessionsResponse } from "@/types/TSessionsResponse";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { PageSizeOption } from "./usePageSize";
import type { StatusFilter } from "./useSessionFilters";

export function useSessions(initialPageSize: PageSizeOption = 10) {
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState<PageSizeOption>(initialPageSize);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  
  const {
    data: sessionsResponse,
    error,
    isLoading,
    refetch,
  } = useQuery<TSessionsResponse>({
    queryKey: ["sessions", pageIndex, pageSize, statusFilter],
    queryFn: () => fetchSessions({ 
      page: pageIndex, 
      pageSize,
      status: statusFilter !== "all" ? statusFilter : undefined
    }),
  });

  const handlePreviousPage = () => {
    setPageIndex((old) => Math.max(old - 1, 1));
  };

  const handleNextPage = () => {
    setPageIndex((old) => old + 1);
  };
  
  const handlePageChange = (page: number) => {
    setPageIndex(page);
  };
  
  const handleStatusChange = (status: StatusFilter) => {
    setStatusFilter(status);
    // Reset to first page when changing filters
    if (pageIndex !== 1) setPageIndex(1);
  };
  
  const handlePageSizeChange = (newSize: PageSizeOption) => {
    setPageSize(newSize);
    // Reset to first page when changing page size
    if (pageIndex !== 1) setPageIndex(1);
    refetch();
  };

  return {
    sessionsResponse,
    error,
    isLoading,
    pageIndex,
    pageSize,
    statusFilter,
    refetch,
    actions: {
      previousPage: handlePreviousPage,
      nextPage: handleNextPage,
      goToPage: handlePageChange,
      setStatusFilter: handleStatusChange,
      setPageSize: handlePageSizeChange,
      refetch,
    },
  };
}